import { ArrowLeft, PartyPopper } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The terminal screen after the meeting was ended for everyone (the unified room bars): a
 * moderator sent the `end` moderate action, the hub broadcast meeting_ended and deleted the
 * SFU room, and every client lands here. The actor reads their own decision wording; everyone
 * else reads who ended the room. No auto-rejoin - the room session is over; the next session
 * starts fresh.
 */
export interface EndedScreenProps {
  /** Identity of the staff member who ended the meeting (the meeting_ended frame). */
  readonly by: string;
  /** Their display name, for the «X ended the meeting» line. */
  readonly byName: string;
  /** The local participant's own identity, to word the actor's screen differently. */
  readonly ownIdentity: string | null;
}

export function EndedScreen({ by, byName, ownIdentity }: EndedScreenProps): JSX.Element {
  const { t } = useTranslation();
  const own = ownIdentity === by;
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-foreground">
      <div
        data-testid="meeting-ended-screen"
        className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-white/10 bg-zinc-900/60 p-8 text-center shadow-2xl shadow-black/50"
      >
        <div className="grid h-12 w-12 place-items-center rounded-full bg-white/[0.06]">
          <PartyPopper className="h-6 w-6 text-zinc-300" />
        </div>
        <h1 className="text-xl font-semibold text-zinc-100">
          {own ? t('room.ended_own_title') : t('room.ended_title')}
        </h1>
        <p className="text-sm text-zinc-400">
          {own ? t('room.ended_own_body') : t('room.ended_body', { name: byName })}
        </p>
        <a
          href="/"
          className="mt-2 flex items-center gap-1.5 rounded-full bg-zinc-800 px-4 py-2 text-sm font-medium text-zinc-100 transition-colors hover:bg-zinc-700"
        >
          <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
          {t('room.back_home')}
        </a>
      </div>
    </main>
  );
}
